/**
 * BFF Client — Typed fetch() wrapper for Hono BFF endpoints.
 *
 * Replaces direct Server Action calls with HTTP requests to the Hono BFF.
 * All requests include credentials (HTTP-only auth cookie) automatically.
 *
 * Both the POST `endpoint`/`body` AND the response are keyed by the two-sided
 * `BffContract` (the SSOT for every BFF round-trip), so an unknown endpoint, a
 * body that doesn't match what the server reads, OR a response shape that drifts
 * from what the server returns is a COMPILE error — the listings-search class of
 * "client/server silently drifted from the proto" can't recur on either side.
 * There is no free caller-supplied response generic: the contract supplies it.
 *
 * The response is PARSED, not cast (R5 W6 PR-4): every body is validated against
 * the contract's runtime twin (`bff-schemas.ts`, compiler-proven identical to
 * `BffContract`). A response that doesn't match the contract becomes a
 * structured `{ success: false, error }` result (or a thrown error for
 * `bffGet`) instead of silently flowing into typed code. Parsing the error
 * envelope also FORWARDS the server's structured `ServerErrorInfo` on non-2xx
 * responses — the old wrapper collapsed every non-2xx to `HTTP <status>`,
 * discarding the typed `reason` the route had decoded for localization.
 */
import type { ServerErrorInfo } from '@letsgethome/lib/server-errors';
import * as z from 'zod/mini';

import type {
  BffBodyArgs,
  BffContract,
  BffEndpoint,
  BffGetContract,
  BffGetEndpoint,
  BffResponse,
} from './bff-contract';
import {
  bffGetResponseSchema,
  bffResponseSchema,
  serverErrorInfoSchema,
  summarizeSchemaError,
} from './bff-schemas';

export interface BffResult<T = unknown> {
  success: boolean;
  data?: T;
  /**
   * Structured server error: the English `message` + the typed `reason` +
   * `metadata` decoded from `common.v1.ErrorInfo` and forwarded by the BFF route
   * (the lossy `error: string` is gone). Consumers localize via the typed
   * `reason` (`localizeServerError` / `localizeApiError`); the union typing makes
   * reading `error.reason` compile-checked.
   */
  error?: ServerErrorInfo;
}

/**
 * The wire envelope, validated: `{ success: true, data? }` (data checked against
 * the endpoint's contract response schema) or `{ success: false, error }`.
 * Discriminated on `success`, so a success envelope with an off-contract `data`
 * fails parsing instead of leaking an unchecked shape to the caller.
 */
function envelopeSchema<E extends BffEndpoint>(endpoint: E) {
  return z.discriminatedUnion('success', [
    z.object({ success: z.literal(true), data: z.optional(bffResponseSchema(endpoint)) }),
    z.object({ success: z.literal(false), error: serverErrorInfoSchema }),
  ]);
}

/**
 * The CLIENT-side failure envelope, in one place.
 *
 * The server mints its envelopes through `fail()` + `emit()` (`bff-route.ts`);
 * these are the two the client has to mint itself, for the responses that never
 * carried a valid envelope at all. Three hand-written object literals of the
 * same shape is how one of them drifts from `serverErrorInfoSchema` without
 * anything noticing.
 */
function bffFailure<T>(message: string): BffResult<T> {
  return { success: false, error: { message } };
}

/** Read a response body as JSON, or `undefined` when it isn't JSON at all. */
async function readJson(res: Response): Promise<unknown> {
  try {
    return (await res.json()) as unknown;
  } catch {
    return undefined;
  }
}

export async function bff<E extends BffEndpoint>(
  endpoint: E,
  ...[body]: BffBodyArgs<E>
): Promise<BffResult<BffResponse<E>>> {
  return bffRequest(endpoint, body);
}

/**
 * `bff()` with the body as a PLAIN parameter — the same round-trip, for a
 * caller whose endpoint is a type PARAMETER rather than a literal. The variadic
 * `BffBodyArgs<E>` on `bff()` is call-site sugar (a no-body endpoint takes no
 * argument); it is an unresolved conditional for a generic `E`, so a generic
 * caller — `NativeForm`, whose endpoint is its registry row's — cannot spell
 * the argument tuple at all. This door takes `BffContract[E]['request']`
 * directly, which for a `request: undefined` endpoint is `undefined`, exactly
 * what `bff()` forwards.
 */
export async function bffRequest<E extends BffEndpoint>(
  endpoint: E,
  body: BffContract[E]['request']
): Promise<BffResult<BffResponse<E>>> {
  const res = await fetch(endpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  // Parse the envelope REGARDLESS of HTTP status: the route emits its structured
  // `{ success: false, error }` envelope with a non-2xx status, and dropping it
  // on `!res.ok` would strip the typed `reason` consumers localize with.
  const parsed = envelopeSchema(endpoint).safeParse(await readJson(res));
  if (parsed.success) {
    return parsed.data;
  }
  if (!res.ok) {
    // Non-2xx without a valid envelope (proxy error page, non-BFF middleware).
    return bffFailure(`HTTP ${res.status}`);
  }
  // A 2xx whose body is off-contract: fail LOUD with a structured error — never
  // hand an unvalidated shape to the typed caller (the old cast did exactly that).
  return bffFailure(
    `Malformed BFF response from ${endpoint}: ${summarizeSchemaError(parsed.error)}`
  );
}

export async function bffGet<E extends BffGetEndpoint>(endpoint: E): Promise<BffGetContract[E]> {
  const res = await fetch(endpoint, {
    method: 'GET',
    credentials: 'include',
  });
  if (!res.ok) {
    throw new Error(`HTTP ${res.status}`);
  }
  // GET handlers return the view-model directly (no `BffResult` envelope); the
  // contract's runtime schema validates the body at this single parse boundary.
  const parsed = bffGetResponseSchema(endpoint).safeParse(await readJson(res));
  if (!parsed.success) {
    throw new Error(
      `Malformed BFF response from ${endpoint}: ${summarizeSchemaError(parsed.error)}`
    );
  }
  // `parsed.data` IS `BffGetContract[E]`: `BffGetSchemaParityGate` proves every
  // endpoint's `z.infer<schema>` EXACTLY equal to its contract entry. The
  // compiler cannot see it here only because `E` is unresolved, so it cannot
  // distribute `z.infer` over the schema union — a limitation of generic
  // inference, not a gap in the guarantee, and it evaporates at every call
  // site, where `E` is a literal.
  //
  // The alternative fixes are worse: annotating `BFF_GET_SCHEMAS` in contract
  // terms would make the parity gate compare a type against itself and pass
  // vacuously (see `bffGetResponseSchema`), and a double cast would erase the
  // relationship entirely.
  return parsed.data as BffGetContract[E];
}
